/**
 * Las clases de un campo. Una sola vez, para toda la app.
 *
 * ── De dónde sale ───────────────────────────────────────────────────────────
 *
 * Había 15 archivos con su propio `inputClass` o `fieldBase` escrito a mano, y
 * entre ellos 12 versiones distintas. No eran 12 diseños: era el mismo diseño
 * escrito 12 veces. Todas querían fondo blanco, borde `acero-300`,
 * `rounded-xl` y `text-sm`; solo discrepaban en el alto y el relleno.
 *
 * Dos de esos archivos —`SetupWizard` y `LeadDetailPanel`— ya habían inventado
 * cada uno por su cuenta un `fieldBase` compartido, sin saber del otro. El
 * equipo ya quería esto; solo faltaba escribirlo donde lo encontraran.
 *
 * Los valores NO son una elección mía: son los que ya usaba `Input`, que a su
 * vez coincide con la mayoría de las 12 copias.
 *
 * ── Por qué son DOS variantes y no una ──────────────────────────────────────
 *
 * Al comparar las 12 apareció que una es distinta a propósito:
 * `EstimateLineItems` usa campos sin fondo ni borde, que solo se dibujan al
 * pasar el cursor. No es un descuido — es una celda editable dentro de una
 * lista densa, y si tuviera el marco de un campo de formulario, una lista de
 * veinte partidas se convertiría en una reja.
 *
 * Aplastarla contra el campo normal habría empeorado esa pantalla en nombre de
 * la coherencia. Así que el sistema le pone nombre en vez de borrarla:
 * `campo()` para formularios, `campoEnLinea()` para celdas.
 */

/**
 * Lo que comparten las dos variantes.
 *
 * SIN el tamaño de letra: va aparte porque hay campos que piden otro (la
 * descripción de una partida es de 15px), y dos utilidades de tamaño en el
 * mismo elemento las resuelve el ORDEN DE LA HOJA de Tailwind, no el orden en
 * que se escriban. Es decir: ponerlas las dos es jugársela. Con `tamaño` solo
 * sale una.
 *
 * Y sin `background-color` en la transición: el campo de formulario tiene fondo
 * fijo y no lo necesita. Lo añade `campoEnLinea`, que sí cambia de fondo.
 */
const COMUN =
    'text-navy-950 rounded-xl border ' +
    'placeholder:text-acero-400 focus:outline-none';

/** El foco: la firma de la casa — borde navy y halo amarillo. */
const FOCO = 'focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55';

/** Con error el halo se vuelve rojo, pero el campo NO pierde su valor. */
const ERROR = 'border-peligro focus:border-peligro focus:ring-2 focus:ring-peligro/25';

/**
 * El alto.
 *
 * `tactil` no es otro diseño: es el mismo campo a 44px en el teléfono, que es
 * el objetivo táctil mínimo, y a 40 desde `sm`. Lo usaba `LeadDetailPanel` y se
 * conserva porque tenía razón, no porque sea una excepción.
 *
 * `auto` es para lo que crece con su contenido —un `<textarea>`— o para
 * columnas estrechas donde fijar el alto rompe la rejilla.
 */
export type AltoDeCampo = 'normal' | 'tactil' | 'auto';

const ALTO: Record<AltoDeCampo, string> = {
    normal: 'h-10',
    tactil: 'h-11 sm:h-10',
    auto: 'py-2.5',
};

/**
 * Los tres de arriba, o una clase de alto propia.
 *
 * `ServiceContractModal` usa `h-11` fijo, sin el `sm:h-10` de `tactil`. Meterlo
 * por `extra` obligaría a pedir `auto` para que no saliera `h-10`, y entonces
 * el campo se llevaría de propina un `py-2.5` que no tenía. Pasarlo por aquí
 * lo deja EXACTO, y sigue sin poder haber dos alturas en el mismo elemento.
 *
 * El `(string & {})` no es adorno: sin él, TypeScript colapsa la unión a
 * `string` y se pierde el autocompletado de los tres nombres.
 */
type Alto = AltoDeCampo | (string & {});

const claseDeAlto = (a: Alto): string => ALTO[a as AltoDeCampo] ?? a;

interface OpcionesDeCampo {
    alto?: Alto;
    /** Pinta el estado de error. El texto del error lo pone quien llama. */
    error?: boolean;
    /**
     * El relleno horizontal. Por defecto `px-4`.
     *
     * Se deja abierto porque un campo con ícono necesita hueco para él
     * (`pl-10`), y eso solo lo sabe quien lo dibuja.
     */
    relleno?: string;
    /**
     * El tamaño de letra. Por defecto `text-sm`.
     *
     * Es una opción y no algo que se meta en `extra` para que NUNCA salgan dos
     * utilidades de tamaño en el mismo elemento: cuál gana lo decide el orden
     * de la hoja de Tailwind, no el del atributo, así que la que se ve no es
     * necesariamente la última que escribiste.
     */
    tamaño?: string;
    /**
     * El ancho. Por defecto `w-full`.
     *
     * Opción por el mismo motivo que `tamaño`: hay campos de ancho fijo —el de
     * horas de `EmployeeDetailModal` es `w-44`— y `w-full w-44` en el mismo
     * elemento es otra pelea que resuelve el orden de la hoja de Tailwind, no
     * quien escribe. Así solo sale uno.
     */
    ancho?: string;
    /** Lo que añada quien llama: fuente de datos, resize, lo que sea. */
    extra?: string;
}

/**
 * El campo de formulario: fondo blanco y borde visible siempre.
 *
 * Se devuelve una cadena de clases y no un componente a propósito. Cambiar
 * `<input className={inputClass}>` por `<Input>` mueve el DOM —el componente
 * envuelve en un `div` con su etiqueta— y eso descoloca rejillas que hoy
 * funcionan. Esta ola unifica el ASPECTO, que es lo que estaba roto; adoptar
 * el componente se hace pantalla por pantalla, mirándolas.
 */
export function campo({ alto = 'normal', error = false, relleno = 'px-4', tamaño = 'text-sm', ancho = 'w-full', extra = '' }: OpcionesDeCampo = {}): string {
    return [
        COMUN,
        'bg-white transition-[border-color,box-shadow] duration-150',
        ancho,
        tamaño,
        claseDeAlto(alto),
        relleno,
        error ? ERROR : `border-acero-300 ${FOCO}`,
        extra,
    ].filter(Boolean).join(' ');
}

/**
 * La celda editable: sin fondo ni borde hasta que la tocas.
 *
 * Para listas densas donde cada fila lleva varios campos. El marco aparece al
 * pasar el cursor, así que se sabe que se puede escribir, pero veinte filas no
 * se ven como una reja.
 */
export function campoEnLinea({ alto = 'normal', relleno = 'px-3', tamaño = 'text-sm', ancho = 'w-full', extra = '' }: Omit<OpcionesDeCampo, 'error'> = {}): string {
    return [
        COMUN,
        ancho,
        // Esta sí cambia de fondo al pasar el cursor, así que su transición
        // incluye `background-color`.
        'bg-transparent border-transparent transition-[background-color,border-color,box-shadow] duration-150',
        'hover:bg-acero-50 hover:border-acero-200',
        'focus:bg-white',
        tamaño,
        claseDeAlto(alto),
        relleno,
        FOCO,
        extra,
    ].filter(Boolean).join(' ');
}
